import { useState } from "react";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { FieldValue, fieldControlClass, fieldDescribedBy } from "./FieldValue";
import { ImageFieldProps } from "./ImageField.types";
import { readImageFile } from "./readImageFile";

/** Image editable field: choose or drop a file, see it, remove it. The value is a data URI. */
export function ImageField({
  label,
  name,
  value,
  onChange,
  disabled,
  error,
  className,
  helpText,
}: ImageFieldProps) {
  const [fileError, setFileError] = useState("");
  const shownError = fileError || error;

  const take = async (file: File | undefined) => {
    if (!file) {
      return;
    }

    const result = await readImageFile(file);

    if ("error" in result) {
      setFileError(result.error);
      return;
    }

    setFileError("");
    onChange(result.dataUri);
  };

  if (value) {
    return (
      <div className={classNames("field-value position-relative d-inline-block mw-100 mb-3", className)}>
        <div className="border rounded-2 bg-body-tertiary p-2 text-center">
          <img src={value} alt={label} className="mw-100 mh-160 object-fit-contain" />
        </div>
        {!disabled && (
          <button
            type="button"
            className="btn btn-sm position-absolute top-0 end-0 m-2 bg-white border shadow-none text-body-secondary hover-text-danger d-flex align-items-center justify-content-center rounded-1 p-1-5 lh-1"
            aria-label={`Remove ${label}`}
            title="Delete"
            onClick={() => onChange("")}
          >
            <FontAwesomeIcon icon={faTrashCan} className="small" />
          </button>
        )}
      </div>
    );
  }

  return (
    <FieldValue label={label} htmlFor={name} helpText={helpText} error={shownError} className={className}>
      <div
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => {
          event.preventDefault();
          if (!disabled) {
            take(event.dataTransfer.files[0]);
          }
        }}
      >
        <input
          id={name}
          aria-describedby={fieldDescribedBy(name, helpText, shownError)}
          aria-invalid={shownError ? true : undefined}
          name={name}
          type="file"
          accept="image/*"
          className={fieldControlClass("form-control", shownError)}
          onChange={(event) => take(event.target.files?.[0])}
          disabled={disabled}
        />
      </div>
    </FieldValue>
  );
}
